import { View } from "@/components";
import type { JSX } from "react";

import type { GridProps } from "@/types";
import cn from "@/utils/cn";

/**
 * Grid Component
 *
 * @description
 * Organism-level component that renders a responsive grid layout of items.
 * It is a generic component that accepts an array of items and a render function
 * to completely customize how each card or item is displayed.
 *
 * Use this component when you need to display lists of cards (users, projects, courses, etc.)
 * maintaining a consistent responsive grid structure throughout the application.
 *
 * @component
 * @template T
 *
 * @param {GridProps<T>} props - The component props.
 * @param {T[]} props.items - Array of data items to be rendered.
 * @param {(item: T) => React.ReactNode} props.renderItem - Function to render each item.
 * @param {string} [props.className] - Optional additional CSS classes for the grid container (useful for redefining grid-cols).
 * @param {string} [props.itemClassName] - Optional additional CSS classes for each item wrapper.
 *
 * @returns {JSX.Element} A grid container with rendered items.
 *
 * @example
 * ```tsx
 * interface User { id: number; name: string; }
 * const users = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }];
 *
 * <Grid
 *   items={users}
 *   className="xl:grid-cols-4 lg:grid-cols-3"
 *   renderItem={(user) => <UserCard user={user} />}
 * />
 * ```
 */

const Grid = <T,>({
  items,
  renderItem,
  children,
  className = "",
  itemClassName = "",
  keyExtractor
}: GridProps<T>): JSX.Element => (
  <View className={cn("grid", className)}>
    {children}
    {items &&
      renderItem &&
      items.map((item, index: number) => (
        <View
          key={keyExtractor ? keyExtractor(item, index) : index}
          className={itemClassName}
        >
          {renderItem(item)}
        </View>
      ))}
  </View>
);

export default Grid;
